import type { UserNonFundingLedgerUpdate } from '@nktkas/hyperliquid';
import { useQuery } from '@tanstack/react-query';

import { getInfoClient } from '@/lib/hooks/useHyperClient';
import { HexString } from '../config/wagmi';

const infoClient = getInfoClient();

export interface UseLedgerHistoryProps {
  user: HexString;
}

/**
 * Hook to fetch user ledger history from Hyperliquid
 * @param user - User's wallet address
 * @returns Query result with user ledger history data
 */
export function useLedgerHistory(user: HexString) {
  // Calculate start time as one month ago
  const oneMonthAgo = Date.now() - 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

  return useQuery<UserNonFundingLedgerUpdate[]>({
    queryKey: ['ledgerHistory', user],
    queryFn: async () => {
      const result = await infoClient.userNonFundingLedgerUpdates({
        user,
        startTime: oneMonthAgo,
      });
      return result;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes (ledger updates less frequently than orders)
  });
}

/**
 * Hook to fetch user ledger history with custom start time
 * @param user - User's wallet address
 * @param startTime - Custom start time in milliseconds
 * @returns Query result with user ledger history data
 */
export function useLedgerHistoryWithStartTime(
  user: HexString,
  startTime: number,
) {
  return useQuery<UserNonFundingLedgerUpdate[]>({
    queryKey: ['ledgerHistory', user, startTime],
    queryFn: async () => {
      const result = await infoClient.userNonFundingLedgerUpdates({
        user,
        startTime,
      });
      return result;
    },
    enabled: !!user && !!startTime,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
